import * as React from 'react';
import { Link, type LinkProps } from '@remix-run/react';
import { cn } from '@se-platform/ui/utils';

// The brand has one button and it is black; the outline is its only other form.
// Coloured buttons are deliberately absent (F0 foundations: "there is no coloured button").
const BUTTON_VARIANT_CLASSES = {
  primary: 'border border-brand-black bg-brand-black text-brand-paper hover:bg-brand-ink',
  outline: 'border border-brand-black bg-brand-paper text-brand-black hover:bg-brand-ground',
} as const;

interface WayInButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof BUTTON_VARIANT_CLASSES;
}

/**
 * The action button of the "way in" screens. Defaults to a submit button so a
 * screen's single primary action posts its form.
 */
export const WayInButton = React.forwardRef<HTMLButtonElement, WayInButtonProps>(
  ({ variant = 'primary', className, type = 'submit', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center rounded-button px-[30px] py-3.5 font-display text-brand-button transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-40',
        BUTTON_VARIANT_CLASSES[variant],
        className,
      )}
      {...props}
    />
  ),
);
WayInButton.displayName = 'WayInButton';

/**
 * A text link in the brand's Primary blue. The underline is required: the blue
 * alone is 3.0:1 on white, so it never carries a link without one.
 */
export function WayInLink({ className, ...props }: LinkProps) {
  return (
    <Link
      className={cn(
        'text-brand-link text-brand-primary underline underline-offset-[3px] hover:text-brand-navy',
        className,
      )}
      {...props}
    />
  );
}
